import React from 'react';
import styled from '@emotion/styled';
import { Tabs, Tab, Box, Typography, Grid, Switch } from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { EventReceiveType } from '../../../state/modules/location/locationReducer';
import useModalConfirm from '../../../hooks/useModalConfirm';
import {
  useGetControlLocationManagersQuery,
  useRemoveManagerMutation,
  useUpdateLocationControlMutation,
} from '../../../services/control.service';
import { useSnackbar } from '../../../hooks/useSnackbar';
import { useAuth } from '../../../hooks/useAuth';
import { UpdatePhoneNotiDialog } from './dialogs/UpdatePhoneNotiDialog';
import { UpdateManagerDialog } from './dialogs/UpdateManagerDialog';
import { UpdateRegulatoryDialog } from './dialogs/UpdateRegulatoryDialog';
import { selectGatewayControl } from 'state/modules/gateway/gatewayReducer';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: number;
  value: number;
}
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box pt={2}>{children}</Box>}
    </div>
  );
};

const TabLabel = styled(Typography)({ fontWeight: 700, fontSize: '14px', textTransform: 'none' });
const CustomTab = styled(Tab)({
  padding: '12px 25px 12px 0',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '64px',
});

const withEventReceiverType = (items: EventReceiveType[], type: number) => items.map((item) => ({ ...item, type }));
const getLocationManagerTabStorageKey = (locationId: string) => `location-manager-tab-${locationId}`;

const tableBodySx = {
  fontSize: '14px',
  maxHeight: 'calc(100dvh - 550px)',
  overflowY: 'auto',
  overflowX: 'hidden',
};

export const LocationManager = ({
  eventReceivers,
  eventRegulatories,
  refetch,
  locationId,
}: {
  eventReceivers: EventReceiveType[];
  eventRegulatories: EventReceiveType[];
  locationId: string;
  refetch: () => void;
}) => {
  const {
    auth: { currentUser },
  } = useAuth();
  const [value, setValue] = React.useState(() => {
    if (typeof window === 'undefined') return 0;
    const savedTab = window.sessionStorage.getItem(getLocationManagerTabStorageKey(locationId));
    return savedTab ? Number(savedTab) : 0;
  });
  const [openAddPhoneDialog, setOpenAddPhoneDialog] = React.useState(false);
  const [openAddManagerDialog, setOpenAddManagerDialog] = React.useState(false);
  const [openAddRegulatoryDialog, setOpenAddRegulatoryDialog] = React.useState(false);
  const { showModalConfirm, hideModalConfirm } = useModalConfirm();
  const [updateLocationControl] = useUpdateLocationControlMutation();
  const [removeManager] = useRemoveManagerMutation();
  const { data: managers, refetch: refetchManager } = useGetControlLocationManagersQuery({
    agencyId: currentUser?.sub_id ?? '',
    locationId,
  });
  const { setSnackbar } = useSnackbar();
  const gatewayControl = useSelector(selectGatewayControl);
  const gatewayType = gatewayControl?.gateway_type_id;
  const currentDate = dayjs();
  const remaining_days = dayjs(gatewayControl.subscription_end_at).diff(dayjs(currentDate), 'day');
  const normalizedEventReceivers = React.useMemo(() => withEventReceiverType(eventReceivers, 0), [eventReceivers]);
  const normalizedEventRegulatories = React.useMemo(
    () => withEventReceiverType(eventRegulatories, 1),
    [eventRegulatories]
  );
  const mergedEventReceivers = React.useMemo(
    () => [...normalizedEventReceivers, ...normalizedEventRegulatories],
    [normalizedEventReceivers, normalizedEventRegulatories]
  );

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(getLocationManagerTabStorageKey(locationId), String(value));
  }, [locationId, value]);

  const handleDeleteEventReceiver = (indexSelected: number) => {
    const eventReceiver = normalizedEventReceivers[indexSelected];
    const newEventReceivers = normalizedEventReceivers.filter((item, index) => index !== indexSelected);

    showModalConfirm({
      type: 'warning',
      title: 'Xoá người nhận thông báo',
      content: `Bạn có chắc chắn muốn xoá người nhận thông báo ${eventReceiver.name} không?`,
      confirm: {
        text: 'Xoá',
        action: async () => {
          if (currentUser && locationId) {
            updateLocationControl({
              agencyId: currentUser.sub_id,
              locationId: locationId,
              data: { event_receivers: [...newEventReceivers, ...normalizedEventRegulatories] },
            }).then((res) => {
              refetch();
              setSnackbar({ open: true, message: 'Cập nhận dánh sách nhận cảnh báo thành công', severity: 'success' });
            });
          }
          hideModalConfirm();
        },
      },
      cancel: {
        action: hideModalConfirm,
      },
    });
  };

  const handleDeleteRegulatory = (indexSelected: number) => {
    const eventRegulatory = normalizedEventRegulatories[indexSelected];
    const newEventRegulatories = normalizedEventRegulatories.filter((item, index) => index !== indexSelected);

    showModalConfirm({
      type: 'warning',
      title: 'Xoá cơ quan chức năng',
      content: `Bạn có chắc chắn muốn xoá cơ quan chức năng ${eventRegulatory?.name} không?`,
      confirm: {
        text: 'Xoá',
        action: async () => {
          if (currentUser && locationId) {
            updateLocationControl({
              agencyId: currentUser.sub_id,
              locationId: locationId,
              data: { event_receivers: [...normalizedEventReceivers, ...newEventRegulatories] },
            }).then((res) => {
              refetch();
              setSnackbar({ open: true, message: 'Cập nhận dánh sách nhận cảnh báo thành công', severity: 'success' });
            });
          }
          hideModalConfirm();
        },
      },
      cancel: {
        action: hideModalConfirm,
      },
    });
  };

  const handleDeleteManager = (manager: any) => {
    showModalConfirm({
      type: 'warning',
      title: 'Xoá người nhận thông báo',
      content: `Bạn có chắc chắn muốn xoá người quản lý ${manager.username} không?`,
      confirm: {
        text: 'Xoá',
        action: async () => {
          if (currentUser && locationId) {
            removeManager({
              agencyId: currentUser.sub_id,
              locationId: locationId,
              managerId: manager.id,
            }).then((res) => {
              refetchManager();
              setSnackbar({ open: true, message: 'Cập nhận dánh sách người quản lý thành công', severity: 'success' });
            });
          }
          hideModalConfirm();
        },
      },
      cancel: {
        action: hideModalConfirm,
      },
    });
  };

  const handleSaveEnableEventNumber = (eventReceivers: EventReceiveType[]) => {
    if (currentUser && locationId) {
      updateLocationControl({
        agencyId: currentUser.sub_id,
        locationId: locationId,
        data: { event_receivers: [...withEventReceiverType(eventReceivers, 0), ...normalizedEventRegulatories] },
      }).then((res) => {
        refetch();
        setSnackbar({ open: true, message: 'Cập nhận dánh sách nhận cảnh báo thành công', severity: 'success' });
      });
    }
  };

  const handleSaveEnableRegulatoryNumber = (eventRegulatories: EventReceiveType[]) => {
    if (currentUser && locationId) {
      updateLocationControl({
        agencyId: currentUser.sub_id,
        locationId: locationId,
        data: { event_receivers: [...normalizedEventReceivers, ...withEventReceiverType(eventRegulatories, 1)] },
      }).then((res) => {
        refetch();
        setSnackbar({ open: true, message: 'Cập nhận dánh sách nhận cảnh báo thành công', severity: 'success' });
      });
    }
  };

  return (
    <Box pt={1}>
      {openAddPhoneDialog && (
        <UpdatePhoneNotiDialog
          open={openAddPhoneDialog}
          eventReceivers={mergedEventReceivers}
          onClose={() => setOpenAddPhoneDialog(false)}
          onSuccess={refetch}
          locationId={locationId}
        />
      )}
      {openAddManagerDialog && (
        <UpdateManagerDialog
          open={openAddManagerDialog}
          eventReceivers={eventReceivers}
          onClose={() => setOpenAddManagerDialog(false)}
          onSuccess={refetchManager}
          locationId={locationId}
        />
      )}
      {openAddRegulatoryDialog && (
        <UpdateRegulatoryDialog
          open={openAddRegulatoryDialog}
          eventReceivers={mergedEventReceivers}
          onClose={() => setOpenAddRegulatoryDialog(false)}
          onSuccess={refetch}
          locationId={locationId}
        />
      )}
      <Tabs
        value={value}
        onChange={handleChange}
        variant="fullWidth"
        // centered
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <CustomTab
          label={
            <TabLabel sx={{ whiteSpace: 'normal', lineHeight: 1.2, textAlign: 'center' }}>
              DS nhận
              <br />
              thông báo
            </TabLabel>
          }
          value={0}
        />
        <CustomTab label={<TabLabel sx={{ textAlign: 'center' }}>DS người quản lý</TabLabel>} value={1} />
        {gatewayType === "GW-C4G" && (
          <CustomTab
            label={
              <TabLabel sx={{ whiteSpace: 'normal', lineHeight: 1.2, textAlign: 'center' }}>
                DS cơ quan
                <br />
                chức năng
              </TabLabel>
            }
            value={2}
          />
        )}
      </Tabs>
      <TabPanel value={value} index={0}>
        <Box style={{ borderRadius: '6px' }}>
          <Grid
            container
            style={{
              backgroundColor: '#EEF2FA',
              border: '1px solid #C5C6D2',
              height: '48px',
              borderRadius: '6px 6px 0 0',
              fontWeight: 500,
              fontSize: '14px',
              paddingRight: normalizedEventReceivers.length > 3 ? '15px' : 0,
            }}
          >
            <Grid item xs={5} style={{ borderRight: '1px solid #C5C6D2' }}>
              <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                Họ Tên
              </Box>
            </Grid>
            <Grid item xs={7}>
              <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Box>{`SMS`}</Box>
                <Switch
                  size="small"
                  disabled={remaining_days > 0 ? false : true}
                  checked={normalizedEventReceivers.every((item) => item.enabled_sms)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const newEventReceivers = checked
                      ? normalizedEventReceivers.map((item) => ({ ...item, enabled_sms: true, type: 0 }))
                      : normalizedEventReceivers.map((item) => ({ ...item, enabled_sms: false, type: 0 }));
                    handleSaveEnableEventNumber([...newEventReceivers]);
                  }}
                />
                <Box>{`Call`}</Box>
                <Switch
                  size="small"
                  disabled={remaining_days > 0 ? false : true}
                  checked={normalizedEventReceivers.every((item) => item.enabled_call)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const newEventReceivers = checked
                      ? normalizedEventReceivers.map((item) => ({ ...item, enabled_call: true, type: 0 }))
                      : normalizedEventReceivers.map((item) => ({ ...item, enabled_call: false, type: 0 }));
                    handleSaveEnableEventNumber([...newEventReceivers]);
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Grid container sx={tableBodySx}>
            {normalizedEventReceivers.map((item, index) => (
              <>
                <Grid
                  item
                  xs={5}
                  height="40px"
                  style={{
                    borderLeft: '1px solid #C5C6D2',
                    borderRight: '1px solid #C5C6D2',
                    borderBottom: '1px solid #C5C6D2',
                    backgroundColor: index % 2 === 0 ? 'unset' : '#F8F9FC',
                  }}
                >
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      fontWeight: 500,
                      color: '#8F0A0C',
                    }}
                  >
                    {item.name}
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={7}
                  height="40px"
                  style={{
                    borderRight: '1px solid #C5C6D2',
                    borderBottom: '1px solid #C5C6D2',
                    backgroundColor: index % 2 === 0 ? 'unset' : '#F8F9FC',
                  }}
                >
                  <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {`SMS`}
                      <Switch
                        size="small"
                        checked={item.enabled_sms}
                        disabled={remaining_days > 0 ? false : true}
                        onChange={() => {
                          const newItem = { ...item, enabled_sms: !item.enabled_sms, type: 0 };
                          const newEventReceivers = [...normalizedEventReceivers];
                          newEventReceivers[index] = newItem;
                          handleSaveEnableEventNumber([...newEventReceivers]);
                        }}
                      />
                    </Box>

                    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {`Call`}
                      <Switch
                        size="small"
                        checked={item.enabled_call}
                        disabled={remaining_days > 0 ? false : true}
                        onChange={() => {
                          const newItem = { ...item, enabled_call: !item.enabled_call, type: 0 };
                          const newEventReceivers = [...normalizedEventReceivers];
                          newEventReceivers[index] = newItem;
                          handleSaveEnableEventNumber([...newEventReceivers]);
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={5}
                  height="40px"
                  style={{
                    borderLeft: '1px solid #C5C6D2',
                    borderRight: '1px solid #C5C6D2',
                    borderBottom: '1px solid #C5C6D2',
                    backgroundColor: index % 2 === 0 ? 'unset' : '#F8F9FC',
                  }}
                >
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    {item.position || 'Người dùng'}
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={7}
                  height="40px"
                  style={{
                    borderRight: '1px solid #C5C6D2',
                    borderBottom: '1px solid #C5C6D2',
                    backgroundColor: index % 2 === 0 ? 'unset' : '#F8F9FC',
                  }}
                >
                  <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography style={{ marginRight: '16px' }}>{item.phone}</Typography>
                    <DeleteOutline
                      onClick={() => handleDeleteEventReceiver(index)}
                      style={{ color: '#8B8C9B', cursor: 'pointer' }}
                    />
                  </Box>
                </Grid>
              </>
            ))}
          </Grid>
          <Grid
            container
            style={{
              backgroundColor: '#EEF2FA',
              borderLeft: '1px solid #C5C6D2',
              borderRight: '1px solid #C5C6D2',
              borderBottom: '1px solid #C5C6D2',
              height: '48px',
              borderRadius: '0 0 6px 6px',
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            <Grid item xs={12}>
              <Box
                onClick={() => setOpenAddPhoneDialog(true)}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  color: '#0075FF',
                  cursor: 'pointer',
                }}
              >
                Thêm số điện thoại
              </Box>
            </Grid>
          </Grid>
        </Box>
      </TabPanel>

      <TabPanel value={value} index={1}>
        <Box style={{ borderRadius: '6px' }}>
          <Grid
            container
            style={{
              backgroundColor: '#EEF2FA',
              height: '48px',
              border: '1px solid #C5C6D2',
              borderRadius: '6px 6px 0 0',
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            <Grid item xs={5}>
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  borderRight: '1px solid #C5C6D2',
                }}
              >
                Tên Tài khoản
              </Box>
            </Grid>
            <Grid item xs={5}>
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                Số điện thoại
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  borderLeft: '1px solid #C5C6D2',
                }}
              ></Box>
            </Grid>
          </Grid>

          <Grid container sx={tableBodySx}>
            {managers?.map((item: any) => (
              <>
                <Grid
                  item
                  xs={5}
                  height="48px"
                  style={{
                    borderLeft: '1px solid #C5C6D2',
                    borderRight: '1px solid #C5C6D2',
                    borderBottom: '1px solid #C5C6D2',
                  }}
                >
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      fontWeight: 500,
                      color: '#8F0A0C',
                    }}
                  >
                    {item.username}
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={5}
                  height="48px"
                  style={{
                    borderRight: '1px solid #C5C6D2',
                    borderBottom: '1px solid #C5C6D2',
                  }}
                >
                  <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    {item.phone}
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={2}
                  height="48px"
                  style={{
                    borderRight: '1px solid #C5C6D2',
                    borderBottom: '1px solid #C5C6D2',
                  }}
                >
                  <Box
                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
                    onClick={() => handleDeleteManager(item)}
                  >
                    <DeleteOutline style={{ color: '#8B8C9B', cursor: 'pointer' }} />
                  </Box>
                </Grid>
              </>
            ))}
          </Grid>
          <Grid
            container
            style={{
              backgroundColor: '#EEF2FA',
              borderLeft: '1px solid #C5C6D2',
              borderRight: '1px solid #C5C6D2',
              borderBottom: '1px solid #C5C6D2',
              height: '48px',
              borderRadius: '0 0 6px 6px',
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            <Grid item xs={12}>
              <Box
                onClick={() => setOpenAddManagerDialog(true)}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  color: '#0075FF',
                  cursor: 'pointer',
                }}
              >
                Thêm quản lý
              </Box>
            </Grid>
          </Grid>
        </Box>
      </TabPanel>

      <TabPanel value={value} index={2}>
        <Box style={{ borderRadius: '6px' }}>
          <Grid
            container
            style={{
              backgroundColor: '#EEF2FA',
              border: '1px solid #C5C6D2',
              height: '48px',
              borderRadius: '6px 6px 0 0',
              fontWeight: 500,
              fontSize: '14px',
              paddingRight: normalizedEventRegulatories.length > 3 ? '15px' : 0,
            }}
          >
            <Grid item xs={5} style={{ borderRight: '1px solid #C5C6D2' }}>
              <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                Họ Tên
              </Box>
            </Grid>
            <Grid item xs={7}>
              <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Box>{`SMS`}</Box>
                <Switch
                  size="small"
                  disabled={remaining_days > 0 ? false : true}
                  checked={normalizedEventRegulatories.every((item) => item.enabled_sms)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const newEventRegulatories = checked
                      ? normalizedEventRegulatories.map((item) => ({ ...item, enabled_sms: true, type: 1 }))
                      : normalizedEventRegulatories.map((item) => ({ ...item, enabled_sms: false, type: 1 }));
                    handleSaveEnableRegulatoryNumber([...newEventRegulatories]);
                  }}
                />
                <Box>{`Call`}</Box>
                <Switch
                  size="small"
                  disabled={remaining_days > 0 ? false : true}
                  checked={normalizedEventRegulatories.every((item) => item.enabled_call)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const newEventRegulatories = checked
                      ? normalizedEventRegulatories.map((item) => ({ ...item, enabled_call: true, type: 1 }))
                      : normalizedEventRegulatories.map((item) => ({ ...item, enabled_call: false, type: 1 }));
                    handleSaveEnableRegulatoryNumber([...newEventRegulatories]);
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Grid container sx={tableBodySx}>
            {normalizedEventRegulatories.map((item, index) => (
              <>
                <Grid
                  item
                  xs={5}
                  height="40px"
                  style={{
                    borderLeft: '1px solid #C5C6D2',
                    borderRight: '1px solid #C5C6D2',
                    borderBottom: '1px solid #C5C6D2',
                    backgroundColor: index % 2 === 0 ? 'unset' : '#F8F9FC',
                  }}
                >
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      fontWeight: 500,
                      color: '#8F0A0C',
                    }}
                  >
                    {item.name}
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={7}
                  height="40px"
                  style={{
                    borderRight: '1px solid #C5C6D2',
                    borderBottom: '1px solid #C5C6D2',
                    backgroundColor: index % 2 === 0 ? 'unset' : '#F8F9FC',
                  }}
                >
                  <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {`SMS`}
                      <Switch
                        size="small"
                        checked={item.enabled_sms}
                        disabled={remaining_days > 0 ? false : true}
                        onChange={() => {
                          const newItem = { ...item, enabled_sms: !item.enabled_sms, type: 1 };
                          const newEventRegulatories = [...normalizedEventRegulatories];
                          newEventRegulatories[index] = newItem;
                          handleSaveEnableRegulatoryNumber([...newEventRegulatories]);
                        }}
                      />
                    </Box>

                    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {`Call`}
                      <Switch
                        size="small"
                        checked={item.enabled_call}
                        disabled={remaining_days > 0 ? false : true}
                        onChange={() => {
                          const newItem = { ...item, enabled_call: !item.enabled_call, type: 1 };
                          const newEventRegulatories = [...normalizedEventRegulatories];
                          newEventRegulatories[index] = newItem;
                          handleSaveEnableRegulatoryNumber([...newEventRegulatories]);
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={5}
                  height="40px"
                  style={{
                    borderLeft: '1px solid #C5C6D2',
                    borderRight: '1px solid #C5C6D2',
                    borderBottom: '1px solid #C5C6D2',
                    backgroundColor: index % 2 === 0 ? 'unset' : '#F8F9FC',
                  }}
                >
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    {item.position || 'Cơ quan chức năng'}
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={7}
                  height="40px"
                  style={{
                    borderRight: '1px solid #C5C6D2',
                    borderBottom: '1px solid #C5C6D2',
                    backgroundColor: index % 2 === 0 ? 'unset' : '#F8F9FC',
                  }}
                >
                  <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography style={{ marginRight: '16px' }}>{item.phone}</Typography>
                    <DeleteOutline
                      onClick={() => handleDeleteRegulatory(index)}
                      style={{ color: '#8B8C9B', cursor: 'pointer' }}
                    />
                  </Box>
                </Grid>
              </>
            ))}
          </Grid>

          <Grid
            container
            style={{
              backgroundColor: '#EEF2FA',
              borderLeft: '1px solid #C5C6D2',
              borderRight: '1px solid #C5C6D2',
              borderBottom: '1px solid #C5C6D2',
              height: '48px',
              borderRadius: '0 0 6px 6px',
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            <Grid item xs={12}>
              <Box
                onClick={() => setOpenAddRegulatoryDialog(true)}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  color: '#0075FF',
                  cursor: 'pointer',
                }}
              >
                Thêm cơ quan chức năng
              </Box>
            </Grid>
          </Grid>
        </Box>
      </TabPanel>
    </Box>
  );
};
